import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BUSINESS } from "@/i18n/content";

const LangSwitch = ({ compact }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="language-switcher"
      className="inline-flex items-center rounded-full bg-aqua p-1 text-sm font-bold"
    >
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 rounded-full transition-colors ${
          lang === "en" ? "bg-navy text-white" : "text-navy hover:text-brand"
        }`}
      >
        English
      </button>
      <button
        data-testid="lang-es"
        onClick={() => setLang("es")}
        className={`px-3 py-1.5 rounded-full transition-colors ${
          lang === "es" ? "bg-navy text-white" : "text-navy hover:text-brand"
        }`}
      >
        Espa&#241;ol
      </button>
    </div>
  );
};

const Logo = () => (
  <Link to="/" data-testid="logo-home-link" className="flex items-center gap-3 group">
    <div className="w-11 h-11 rounded-2xl bg-navy text-white grid place-items-center font-heading font-extrabold text-lg shadow-sm group-hover:bg-brand transition-colors">
      AL
    </div>
    <div className="leading-tight">
      <span className="block font-heading font-extrabold text-navy text-[15px] tracking-tight">
        Alma Y Lengua
      </span>
      <span className="block text-[10px] uppercase tracking-[0.2em] text-ink/50 font-semibold">
        Bilingual Learning Center
      </span>
    </div>
  </Link>
);

export const Header = () => {
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: tr.nav.home, testid: "home" },
    { to: "/about", label: tr.nav.about, testid: "about" },
    { to: "/services", label: tr.nav.services, testid: "services" },
    { to: "/business-academy", label: tr.nav.academy, testid: "academy" },
    { to: "/guide", label: tr.nav.guide, testid: "guide" },
    { to: "/contact", label: tr.nav.contact, testid: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-shadow ${
        scrolled ? "shadow-[0_4px_30px_rgba(7,20,63,0.06)]" : ""
      } bg-offwhite/80 backdrop-blur-xl border-b border-navy/5`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.testid}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[15px] font-semibold transition-colors ${
                  isActive ? "text-brand" : "text-navy hover:text-brand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LangSwitch />
          <Link
            to="/contact"
            data-testid="header-contact-cta"
            className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-6 py-3 font-semibold hover:bg-navy transition-colors shadow-lg shadow-brand/20"
          >
            <Phone size={16} /> {tr.nav.contactUs}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden w-11 h-11 grid place-items-center rounded-xl bg-navy text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-offwhite border-t border-navy/5 px-5 py-6 space-y-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-xl text-lg font-semibold transition-colors ${
                  isActive ? "bg-aqua text-navy" : "text-navy hover:bg-aqua"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 flex flex-col gap-4">
            <LangSwitch />
            <Link
              to="/contact"
              data-testid="mobile-contact-cta"
              className="inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-6 py-4 font-semibold"
            >
              <Phone size={18} /> {tr.nav.contactUs}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
